import { doc, getDoc } from "firebase/firestore";
import { ChevronRight, HelpCircle, MapPin } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";

import { db } from "integrations/firebase/firestore";

import { AvatarPlaceholder } from "components/AvatarPlaceholder";

import { championLinks } from "../admin/track-champion";

import { DEFAULT_TRACK_COLOR } from "./AgendaSchedule";
import { EventPersonSheet, type EventPersonTarget } from "./EventPersonSheet";
import { speakerSchema, type Track } from "./schemas";
import { useDirectoryAttendee } from "./use-event-content";

/**
 * The attendee's pre-assigned track, named above the agenda filters, with
 * the room, theme question and champion the organiser set on it (TNG-95).
 *
 * The champions (up to two) are each stored as a link and resolved here to their entry as it
 * reads now: the directory row for a roster attendee, the speaker document
 * for a speaker. Both are documents a member may read. A champion who has
 * since left (no directory row, speaker deleted) is simply not shown.
 * Tapping the champion opens the shared person sheet with their id.
 */
export function YourTrackBanner({
  eventId,
  track,
  children,
}: {
  eventId: string;
  track: Track;
  /** Under the track: the table request control while requests are on. */
  children?: ReactNode;
}) {
  const color = track.color ?? DEFAULT_TRACK_COLOR;

  return (
    <div
      className="space-y-2 rounded-md border border-l-4 border-border px-4 py-3 text-sm text-foreground"
      style={{ borderLeftColor: color }}
      data-testid="agenda-your-track"
    >
      <p className="flex items-center gap-2">
        <span
          className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
          style={{ backgroundColor: color }}
          aria-hidden
        />
        <span className="min-w-0">
          Your track: <strong className="font-semibold">{track.name}</strong>
        </span>
      </p>
      {track.room?.trim() && (
        <p
          className="flex items-start gap-2 text-muted-foreground"
          data-testid="agenda-your-track-room"
        >
          <MapPin className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span className="min-w-0 break-words">
            <span className="sr-only">Room: </span>
            {track.room}
          </span>
        </p>
      )}
      {track.question?.trim() && (
        <p
          className="flex items-start gap-2 text-muted-foreground"
          data-testid="agenda-your-track-question"
        >
          <HelpCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span className="min-w-0 whitespace-pre-line break-words">
            <span className="font-medium text-foreground">
              Theme question:{" "}
            </span>
            {track.question}
          </span>
        </p>
      )}
      <TrackChampions
        eventId={eventId}
        track={track}
        testIdPrefix="agenda-your-track"
      />
      {children}
    </div>
  );
}

/**
 * A track's champions as buttons that open their profile sheet: the banner's
 * own block, shared with the cards on the Me tab so that a stale link is
 * handled in one place. Nothing is drawn for a track without a champion.
 * `testIdPrefix` names the buttons `<prefix>-champion` and
 * `<prefix>-second-champion`.
 */
export function TrackChampions({
  eventId,
  track,
  testIdPrefix,
}: {
  eventId: string;
  track: Track;
  testIdPrefix: string;
}) {
  // A track that names the same person twice has one champion.
  const [first, second] = championSlots(track);
  if (!first && !second) return null;
  return (
    <div className="space-y-2">
      {first && (
        <ChampionButton
          eventId={eventId}
          slot={first}
          testId={`${testIdPrefix}-champion`}
        />
      )}
      {second && (
        <ChampionButton
          eventId={eventId}
          slot={second}
          testId={`${testIdPrefix}-second-champion`}
        />
      )}
    </div>
  );
}

interface ChampionSlot {
  attendeeId: string | null;
  speakerId: string | null;
}

/** The track's champions, first then second, the same person only once. */
function championSlots(track: Track): ChampionSlot[] {
  return championLinks(track).map((link) => ({
    attendeeId: link.kind === "attendee" ? link.id : null,
    speakerId: link.kind === "speaker" ? link.id : null,
  }));
}

interface Champion {
  /** What the banner's button shows. */
  person: { name: string; avatar?: string };
  /** What the sheet loads. */
  target: EventPersonTarget;
}

/** One champion's button and profile sheet; nothing while gone or loading. */
function ChampionButton({
  eventId,
  slot,
  testId,
}: {
  eventId: string;
  slot: ChampionSlot;
  testId: string;
}) {
  const champion = useChampion(eventId, slot);
  const [open, setOpen] = useState(false);
  if (!champion) return null;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`Track champion ${champion.person.name}, view profile`}
        data-testid={testId}
        className="flex min-h-11 w-full items-center gap-3 rounded-md bg-muted/60 px-3 py-2 text-left transition-colors hover:bg-muted"
      >
        {champion.person.avatar ? (
          <img
            src={champion.person.avatar}
            alt=""
            className="h-10 w-10 shrink-0 rounded-full object-cover"
          />
        ) : (
          <AvatarPlaceholder name={champion.person.name} size="sm" />
        )}
        <span className="min-w-0 flex-1">
          <span className="block text-xs text-muted-foreground">
            Track champion
          </span>
          <span className="block truncate font-medium">
            {champion.person.name}
          </span>
        </span>
        <ChevronRight className="h-4 w-4 shrink-0" aria-hidden />
      </button>
      <EventPersonSheet
        eventId={eventId}
        person={open ? champion.target : null}
        onClose={() => setOpen(false)}
      />
    </>
  );
}

/** One champion as they read now, or null (none, gone, loading). */
function useChampion(eventId: string, slot: ChampionSlot): Champion | null {
  const attendee = useDirectoryAttendee(eventId, slot.attendeeId);
  const speakerId = slot.speakerId;
  const [speaker, setSpeaker] = useState<Champion["person"] | null>(null);

  useEffect(() => {
    setSpeaker(null);
    if (!speakerId) return;
    let cancelled = false;
    getDoc(doc(db, "events", eventId, "speakers", speakerId))
      .then((snapshot) => {
        if (cancelled || !snapshot.exists()) return;
        const result = speakerSchema.safeParse({
          id: snapshot.id,
          ...snapshot.data(),
        });
        if (!result.success) return;
        setSpeaker({ name: result.data.name, avatar: result.data.avatar });
      })
      // A champion is a nicety on the banner; the track still shows.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [eventId, speakerId]);

  if (slot.attendeeId) {
    if (!attendee?.name.trim()) return null;
    return {
      person: { name: attendee.name, avatar: attendee.avatar },
      target: { attendeeId: slot.attendeeId },
    };
  }
  if (!speakerId || !speaker?.name.trim()) return null;
  return { person: speaker, target: { speakerId } };
}
